import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import type { OptionType } from '@/api/common';
import {
  InfiniteComboboxPickerMultiple,
  InfiniteComboboxPickerSingle,
} from '@/components/Form/pickers';
import type { FilterOperatorMulti, FilterOperatorValue } from '@/components/InstantSearch';

import { IncludeExcludeToggle } from '../IncludeExcludeToggle';

type Props<T extends OptionType> = {
  value?: FilterOperatorMulti | null;
  options?: T[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  error?: Error | null;
  mode?: 'single' | 'multiple';
  hasExclude?: boolean;
  placeholder: string;
  loadingPlaceholder?: string;
  notFoundContent: ReactNode;
  renderOption?: (option: T) => ReactNode;
  onSearch?: (search: string) => void;
  searchDebounceMs?: number;
  loadMore?: () => void;
  onChange?: (value: FilterOperatorValue) => void;
};

const InfiniteComboboxPickerFilter = <T extends OptionType>({
  value,
  options,
  isLoading = false,
  isLoadingMore = false,
  hasMore,
  error,
  mode = 'multiple',
  hasExclude = true,
  placeholder,
  loadingPlaceholder,
  notFoundContent,
  renderOption,
  onSearch,
  searchDebounceMs,
  loadMore,
  onChange,
}: Props<T>) => {
  if (value?.operator && value.operator !== 'in' && value.operator !== 'notIn') {
    throw new Error(`Invalid operator: ${value.operator}`);
  }

  const operator = value?.operator ?? 'in';
  const optionsById = useMemo(
    () => new Map((options ?? []).map((option) => [String(option.id), option])),
    [options],
  );
  const [selectedOptionsById, setSelectedOptionsById] = useState<Map<string, T>>(new Map());

  const selectedOptions = useMemo<T[]>(() => {
    const result: T[] = [];
    for (const id of value?.operand ?? []) {
      const option = optionsById.get(String(id)) ?? selectedOptionsById.get(String(id));
      if (option) {
        result.push(option);
      }
    }
    return result;
  }, [optionsById, selectedOptionsById, value?.operand]);
  const pickerOptions = useMemo(() => {
    const merged = new Map(optionsById);
    for (const option of selectedOptions) {
      merged.set(String(option.id), option);
    }
    return Array.from(merged.values());
  }, [optionsById, selectedOptions]);

  return (
    <div className="flex flex-col gap-2">
      {hasExclude && (
        <IncludeExcludeToggle
          operator={operator}
          onChange={(newOperator) => {
            onChange?.({
              operator: newOperator,
              operand: value?.operand ?? [],
            });
          }}
        />
      )}
      {mode === 'single' ? (
        <InfiniteComboboxPickerSingle<T>
          filteredItems={pickerOptions}
          value={selectedOptions[0] ?? null}
          isLoading={isLoading}
          isFetchingNextPage={isLoadingMore}
          hasMore={hasMore}
          error={error}
          placeholder={placeholder}
          loadingPlaceholder={loadingPlaceholder}
          notFoundContent={notFoundContent}
          renderOption={renderOption}
          onSearch={onSearch}
          searchDebounceMs={searchDebounceMs}
          loadMore={loadMore}
          onChange={(selectedValue) => {
            if (!selectedValue) {
              setSelectedOptionsById(new Map());
              onChange?.(null);
              return;
            }

            setSelectedOptionsById(new Map<string, T>([[String(selectedValue.id), selectedValue]]));
            onChange?.({
              operator,
              operand: [selectedValue.id],
            });
          }}
        />
      ) : (
        <InfiniteComboboxPickerMultiple<T>
          filteredItems={pickerOptions}
          value={selectedOptions}
          isLoading={isLoading}
          isFetchingNextPage={isLoadingMore}
          hasMore={hasMore}
          error={error}
          placeholder={placeholder}
          loadingPlaceholder={loadingPlaceholder}
          notFoundContent={notFoundContent}
          renderOption={renderOption}
          onSearch={onSearch}
          searchDebounceMs={searchDebounceMs}
          loadMore={loadMore}
          onChange={(selectedValue) => {
            if (!selectedValue?.length) {
              setSelectedOptionsById(new Map());
              onChange?.(null);
              return;
            }

            setSelectedOptionsById(
              new Map(selectedValue.map((option) => [String(option.id), option])),
            );
            onChange?.({
              operator,
              operand: selectedValue.map((option) => option.id),
            });
          }}
        />
      )}
    </div>
  );
};

export default InfiniteComboboxPickerFilter;
